/**
 * DH Case Builder
 * Multi-case purchase flow for Hippie Water liquidation.
 *
 * Each case = 3 × 8-packs (24 cans), single mg level.
 * Users can build multiple cases, each independently 5mg or 10mg.
 * State: array of cases, each with mg + flavor selections.
 *
 * Cart items tagged with `_hw_case: "true"` line item property.
 */

if (!customElements.get('dh-case-builder')) {
  class DHCaseBuilder extends HTMLElement {
    constructor() {
      super();
      this.products5mg = [];
      this.products10mg = [];
      this.settings = {};
      // State: array of cases. Each case: { mg: '5'|'10', flavors: { handle: qty } }
      // activeCaseIndex: which case is being built (-1 = none, show completed list)
      // phase: 'pick-mg' | 'pick-flavors' | 'review'
      this.state = {
        cases: [],
        activeCaseIndex: -1,
        phase: 'pick-mg', // start by picking mg for first case
      };
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    connectedCallback() {
      this.parseProductData();
      this.restoreState();
      this.bindEvents();
      this.bindLightbox();
      this.initFlavorScroll();
      this.render();
      this.defendAgainstSavedBy();

      // Mobile browser chrome color
      this._originalThemeColor = document.querySelector('meta[name="theme-color"]')?.content || '';
      const tc = document.querySelector('meta[name="theme-color"]');
      if (tc) tc.content = '#002D1E';
    }

    disconnectedCallback() {
      if (this._savedByObserver) this._savedByObserver.disconnect();
      const tc = document.querySelector('meta[name="theme-color"]');
      if (tc && this._originalThemeColor !== undefined) tc.content = this._originalThemeColor;
    }

    /* SavedBy/Checkout+ hijacks buttons with [data-action="checkout"] by
       hiding them (savedby-hidden attr + inline styles) and injecting
       <savedby-checkout-plus> elements. Fight JS with JS: observe mutations
       and undo their changes as they happen. */
    defendAgainstSavedBy() {
      const cleanup = () => {
        // Remove injected SavedBy elements
        this.querySelectorAll('savedby-checkout-plus').forEach(el => el.remove());
        // Restore any buttons SavedBy hid
        this.querySelectorAll('[savedby-hidden]').forEach(el => {
          el.removeAttribute('savedby-hidden');
          el.style.removeProperty('display');
        });
      };

      // Run immediately in case SavedBy already ran
      cleanup();

      // Watch for future mutations (SavedBy injects after page load)
      this._savedByObserver = new MutationObserver(() => cleanup());
      this._savedByObserver.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['savedby-hidden'] });
    }

    /* ========================================
       HELPERS
       ======================================== */

    getProductsForMg(mg) {
      return mg === '10' ? this.products10mg : this.products5mg;
    }

    getCasePrice(mg) {
      return mg === '10' ? this.settings.case_price_10mg : this.settings.case_price_5mg;
    }

    get packsPerCase() {
      return this.settings.packs_per_case || 3;
    }

    get cansPerPack() {
      return this.settings.cans_per_pack || 8;
    }

    get activeCase() {
      if (this.state.activeCaseIndex >= 0 && this.state.activeCaseIndex < this.state.cases.length) {
        return this.state.cases[this.state.activeCaseIndex];
      }
      return null;
    }

    cleanTitle(title) {
      return title
        .replace(/ 5mg$/i, '')
        .replace(/ 10mg$/i, '')
        .replace(/ Energy Drink$/i, '')
        .replace(/ 8-?pack$/i, '');
    }

    formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2);
    }

    getCasePackCount(c) {
      return Object.values(c.flavors).reduce((sum, qty) => sum + qty, 0);
    }

    isCaseComplete(c) {
      return this.getCasePackCount(c) === this.packsPerCase;
    }

    getCompletedCases() {
      return this.state.cases.filter(c => this.isCaseComplete(c));
    }

    getTotalPrice() {
      let total = 0;
      for (const c of this.state.cases) {
        if (this.isCaseComplete(c)) {
          total += this.getCasePrice(c.mg);
        }
      }
      return total;
    }

    getRetailPerPack(mg) {
      return mg === '10'
        ? (this.settings.retail_per_pack_10mg || 3999)
        : (this.settings.retail_per_pack_5mg || 3599);
    }

    getRetailValue() {
      let total = 0;
      for (const c of this.state.cases) {
        if (!this.isCaseComplete(c)) continue;
        const retailPerPack = this.getRetailPerPack(c.mg);
        const packs = this.getCasePackCount(c);
        total += retailPerPack * packs;
      }
      return total;
    }

    getShippingFee() {
      const total = this.getTotalPrice();
      const threshold = this.settings.free_shipping_threshold_cents || 10000;
      if (total >= threshold) return 0;
      return this.settings.shipping_fee_cents || 900;
    }

    /* ========================================
       DATA & STATE
       ======================================== */

    parseProductData() {
      const dataEl = document.getElementById('dh-cb-product-data');
      if (!dataEl) return;
      try {
        const data = JSON.parse(dataEl.textContent);
        this.products5mg = data.products_5mg || [];
        this.products10mg = data.products_10mg || [];
        this.settings = data.settings || {};
      } catch (e) {
        console.error('[CaseBuilder] Failed to parse product data:', e);
      }
    }

    restoreState() {
      // Sale over: drop any half-built case so the shopper can't resume past the mg step
      if (this.hasAttribute('data-sale-ended')) {
        try { sessionStorage.removeItem('dh-case-state'); } catch (e) { /* ignore */ }
        return;
      }
      try {
        const saved = sessionStorage.getItem('dh-case-state');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed.cases && Array.isArray(parsed.cases)) {
            this.state = { ...this.state, ...parsed };
          }
        }
      } catch (e) { /* ignore */ }

      // If no cases exist, start fresh
      if (this.state.cases.length === 0) {
        this.state.phase = 'pick-mg';
        this.state.activeCaseIndex = -1;
      }
    }

    saveState() {
      try {
        sessionStorage.setItem('dh-case-state', JSON.stringify(this.state));
      } catch (e) { /* ignore */ }
    }

    /* ========================================
       ACTIONS
       ======================================== */

    startNewCase(mg) {
      const newCase = { mg, flavors: {} };
      // Initialize all flavors for this mg level to 0
      this.getProductsForMg(mg).forEach(p => {
        newCase.flavors[p.handle] = 0;
      });
      this.state.cases.push(newCase);
      this.state.activeCaseIndex = this.state.cases.length - 1;
      this.state.phase = 'pick-flavors';
      this.saveState();
      this.render();
    }

    updateQuantity(handle, delta) {
      const c = this.activeCase;
      if (!c) return;
      const current = c.flavors[handle] || 0;
      const totalPacks = this.getCasePackCount(c);

      // Don't allow adding past case limit
      if (delta > 0 && totalPacks >= this.packsPerCase) return;

      const next = Math.max(0, current + delta);
      c.flavors[handle] = next;
      this.saveState();
      this.render();

      // Auto-advance when case is complete
      if (this.isCaseComplete(c)) {
        // Brief delay to show the completed state before transitioning
        setTimeout(() => {
          this.state.activeCaseIndex = -1;
          this.state.phase = 'case-complete';
          this.saveState();
          this.render();
        }, 400);
      }
    }

    oneOfEach() {
      const c = this.activeCase;
      if (!c) return;
      const products = this.getProductsForMg(c.mg);
      // Reset all to 0 first
      for (const key in c.flavors) c.flavors[key] = 0;
      // Distribute packs across flavors
      const perFlavor = Math.floor(this.packsPerCase / products.length);
      let remaining = this.packsPerCase;
      products.forEach((p, i) => {
        const qty = i < this.packsPerCase % products.length ? perFlavor + 1 : perFlavor;
        c.flavors[p.handle] = qty;
        remaining -= qty;
      });
      this.saveState();
      this.render();

      // Auto-advance
      if (this.isCaseComplete(c)) {
        setTimeout(() => {
          this.state.activeCaseIndex = -1;
          this.state.phase = 'case-complete';
          this.saveState();
          this.render();
        }, 400);
      }
    }

    editCase(index) {
      if (index < 0 || index >= this.state.cases.length) return;
      this.state.activeCaseIndex = index;
      this.state.phase = 'pick-flavors';
      this.saveState();
      this.render();
    }

    removeCase(index) {
      if (index < 0 || index >= this.state.cases.length) return;
      this.state.cases.splice(index, 1);
      this.state.activeCaseIndex = -1;
      this.state.phase = this.state.cases.length > 0 ? 'case-complete' : 'pick-mg';
      this.saveState();
      this.render();
    }

    addAnotherCase() {
      this.state.activeCaseIndex = -1;
      this.state.phase = 'pick-mg';
      this.saveState();
      this.render();
    }

    goToReview() {
      const completed = this.getCompletedCases();
      if (completed.length === 0) return;
      this.state.activeCaseIndex = -1;
      this.state.phase = 'review';
      this.saveState();
      this.render();
    }

    goBackFromReview() {
      this.state.phase = 'case-complete';
      this.saveState();
      this.render();
    }

    /* ========================================
       EVENT BINDING
       ======================================== */

    bindEvents() {
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-action]');
        if (!btn) return;
        const action = btn.dataset.action;

        if (action === 'select-mg') {
          if (this.hasAttribute('data-sale-ended')) return;
          this.startNewCase(btn.dataset.mg);
        }
        if (action === 'increment' || action === 'decrement') {
          const card = btn.closest('.dh-bb__flavor-card');
          if (!card) return;
          this.updateQuantity(card.dataset.handle, action === 'increment' ? 1 : -1);
        }
        if (action === 'one-of-each') {
          this.oneOfEach();
        }
        if (action === 'add-another') {
          this.addAnotherCase();
        }
        if (action === 'review') {
          this.goToReview();
        }
        if (action === 'back-from-review') {
          this.goBackFromReview();
        }
        if (action === 'edit-case') {
          this.editCase(parseInt(btn.dataset.caseIndex, 10));
        }
        if (action === 'remove-case') {
          this.removeCase(parseInt(btn.dataset.caseIndex, 10));
        }
        if (action === 'checkout') {
          this.checkout();
        }
        if (action === 'back-to-cases') {
          // From pick-flavors back to case list or mg picker
          if (this.activeCase && this.getCasePackCount(this.activeCase) === 0) {
            // Remove the empty case we just started
            this.state.cases.pop();
          }
          this.state.activeCaseIndex = -1;
          this.state.phase = this.state.cases.length > 0 ? 'case-complete' : 'pick-mg';
          this.saveState();
          this.render();
        }
        if (action === 'header-back') {
          if (this.state.phase === 'review') {
            this.goBackFromReview();
          } else if (this.state.phase === 'pick-flavors') {
            // Back to case list / mg picker
            if (this.activeCase && this.getCasePackCount(this.activeCase) === 0) {
              this.state.cases.pop();
            }
            this.state.activeCaseIndex = -1;
            this.state.phase = this.state.cases.length > 0 ? 'case-complete' : 'pick-mg';
            this.saveState();
            this.render();
          } else if (this.state.phase === 'case-complete') {
            // Back to mg picker
            this.state.phase = 'pick-mg';
            this.saveState();
            this.render();
          } else if (this.state.phase === 'pick-mg' && this.state.cases.length > 0) {
            // Back to case list if they have cases
            this.state.phase = 'case-complete';
            this.saveState();
            this.render();
          } else {
            // No cases, at start — exit to home
            window.location.href = '/';
          }
        }
      });

      // Card click → focus (preview media)
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-action]')) return;
        const card = e.target.closest('.dh-bb__flavor-card');
        if (!card) return;
        const handle = card.dataset.handle;
        if (handle) this.setFocus(handle);
      });
    }

    setFocus(handle) {
      if (this._focusedHandle === handle) return;
      this._focusedHandle = handle;
      this.querySelectorAll('.dh-bb__flavor-card').forEach(card => {
        card.classList.toggle('is-focused', card.dataset.handle === handle);
      });
      this.renderMediaStack(handle);
      this.updateFlavorDots();
    }

    /* ========================================
       MAIN RENDER
       ======================================== */

    render() {
      const phase = this.state.phase;

      // Header
      this.renderHeader();

      // Hide all phases, show active
      this.querySelectorAll('[data-phase]').forEach(el => {
        el.hidden = el.dataset.phase !== phase;
      });

      // Phase-specific rendering
      if (phase === 'pick-mg') {
        // Nothing dynamic needed — static HTML
      } else if (phase === 'pick-flavors') {
        this.renderPickFlavors();
      } else if (phase === 'case-complete') {
        this.renderCaseList();
      } else if (phase === 'review') {
        this.renderReview();
      }

      // Bottom bar
      this.renderBottomBar();
    }

    renderHeader() {
      const title = this.querySelector('[data-header-title]');
      const backBtn = this.querySelector('[data-action="header-back"]');
      const headerNext = this.querySelector('.dh-cb__header-next');

      if (this.state.phase === 'pick-mg' && this.state.cases.length === 0) {
        if (backBtn) backBtn.style.visibility = 'visible';
        if (headerNext) headerNext.hidden = true;
      } else if (this.state.phase === 'pick-flavors') {
        if (backBtn) backBtn.style.visibility = 'visible';
        if (headerNext) headerNext.hidden = true;
      } else if (this.state.phase === 'case-complete') {
        if (backBtn) backBtn.style.visibility = 'visible';
        if (headerNext) {
          headerNext.hidden = false;
          headerNext.disabled = this.getCompletedCases().length === 0;
          headerNext.innerHTML = 'Review <span aria-hidden="true">&rarr;</span>';
          headerNext.dataset.action = 'review';
        }
      } else if (this.state.phase === 'review') {
        if (backBtn) backBtn.style.visibility = 'visible';
        if (headerNext) {
          headerNext.hidden = false;
          headerNext.disabled = false;
          headerNext.innerHTML = 'Checkout <span aria-hidden="true">&rarr;</span>';
          headerNext.dataset.action = 'checkout';
        }
      }

      // Progress bar
      const fill = this.querySelector('[data-progress-fill]');
      if (fill) {
        const phases = { 'pick-mg': 25, 'pick-flavors': 50, 'case-complete': 75, 'review': 100 };
        fill.style.width = (phases[this.state.phase] || 25) + '%';
      }
    }

    /* ========================================
       PICK FLAVORS PHASE
       ======================================== */

    renderPickFlavors() {
      const c = this.activeCase;
      if (!c) return;

      const container = this.querySelector('[data-phase="pick-flavors"]');
      if (!container) return;

      const products = this.getProductsForMg(c.mg);
      const totalPacks = this.getCasePackCount(c);
      const remaining = this.packsPerCase - totalPacks;
      const caseNum = this.state.activeCaseIndex + 1;

      // Update heading
      const heading = container.querySelector('[data-flavors-heading]');
      if (heading) heading.textContent = `Case ${caseNum}: ${c.mg}mg THC`;

      const subheading = container.querySelector('[data-flavors-subheading]');
      if (subheading) {
        if (remaining > 0) {
          subheading.textContent = `Pick ${remaining} more pack${remaining !== 1 ? 's' : ''} to fill your case`;
        } else {
          subheading.textContent = 'Case complete!';
        }
      }

      // Case tracker slots
      const slotsContainer = container.querySelector('[data-case-slots]');
      if (slotsContainer) {
        slotsContainer.innerHTML = '';
        for (let i = 0; i < this.packsPerCase; i++) {
          const slot = document.createElement('div');
          slot.className = 'dh-cb__case-slot';
          if (i < totalPacks) slot.classList.add('is-filled');
          else if (i === totalPacks) slot.classList.add('is-active');
          slotsContainer.appendChild(slot);
        }
      }

      // Show/hide correct flavor group
      container.querySelectorAll('[data-flavor-group]').forEach(group => {
        group.hidden = group.dataset.flavorGroup !== c.mg;
      });

      // Calculate per-pack case price for display
      const casePrice = this.getCasePrice(c.mg);
      const casePricePerPack = Math.round(casePrice / this.packsPerCase);
      const retailPerPack = this.getRetailPerPack(c.mg);

      // Update flavor cards for this mg level
      products.forEach(p => {
        const cards = container.querySelectorAll(`.dh-bb__flavor-card[data-handle="${p.handle}"]`);
        cards.forEach(card => {
          const qty = c.flavors[p.handle] || 0;
          const qtyValue = card.querySelector('.dh-bb__qty-value');
          const addBtn = card.querySelector('.dh-bb__add-btn');
          const qtyInline = card.querySelector('.dh-bb__qty-inline');

          if (qtyValue) qtyValue.textContent = qty;

          if (addBtn && qtyInline) {
            if (qty > 0) {
              addBtn.style.display = 'none';
              qtyInline.style.display = 'inline-flex';
            } else {
              addBtn.style.display = '';
              qtyInline.style.display = 'none';
            }
            // Disable add if case is full
            if (totalPacks >= this.packsPerCase && qty === 0) {
              addBtn.disabled = true;
              addBtn.style.opacity = '0.35';
            } else {
              addBtn.disabled = false;
              addBtn.style.opacity = '';
            }
          }

          // Update price display with savings
          const priceEl = card.querySelector('.dh-bb__flavor-price');
          if (priceEl) {
            priceEl.innerHTML = `<span class="dh-cb__card-price-new">${this.formatMoney(casePricePerPack)}/8-pack</span> <del class="dh-cb__card-price-was">${this.formatMoney(retailPerPack)}</del>`;
          }

          card.classList.toggle('is-selected', qty > 0);
        });
      });

      // Render flavor dots for this mg level
      this.renderFlavorDots(c.mg);

      // Focus first product if none focused for this mg level
      if (!this._focusedHandle || !products.some(p => p.handle === this._focusedHandle)) {
        const first = products.find(p => p.available) || products[0];
        if (first) this._focusedHandle = first.handle;
      }

      // Always apply is-focused class to the correct card (may have been set before DOM was visible)
      if (this._focusedHandle) {
        container.querySelectorAll('.dh-bb__flavor-card').forEach(card => {
          card.classList.toggle('is-focused', card.dataset.handle === this._focusedHandle);
        });
        this.renderMediaStack(this._focusedHandle);
      }
    }

    /* ========================================
       CASE LIST PHASE (after completing a case)
       ======================================== */

    renderCaseList() {
      const container = this.querySelector('[data-phase="case-complete"]');
      if (!container) return;

      const listEl = container.querySelector('[data-case-list]');
      if (!listEl) return;

      listEl.innerHTML = '';

      this.state.cases.forEach((c, i) => {
        if (!this.isCaseComplete(c)) return;

        const products = this.getProductsForMg(c.mg);
        const casePrice = this.getCasePrice(c.mg);

        const card = document.createElement('div');
        card.className = 'dh-cb__completed-case';

        let flavorLines = '';
        for (const [handle, qty] of Object.entries(c.flavors)) {
          if (qty <= 0) continue;
          const product = products.find(p => p.handle === handle);
          if (product) {
            flavorLines += `<div class="dh-cb__case-flavor-row">
              <span>${qty}× ${this.cleanTitle(product.title)}</span>
              <span class="dh-cb__case-flavor-cans">${qty * this.cansPerPack} cans</span>
            </div>`;
          }
        }

        const retailPerPack = this.getRetailPerPack(c.mg);
        const retailTotal = retailPerPack * this.packsPerCase;
        const caseSavings = retailTotal - casePrice;
        const savingsPct = Math.round((caseSavings / retailTotal) * 100);

        card.innerHTML = `
          <div class="dh-cb__case-header-row">
            <h3 class="dh-cb__case-title">Case ${i + 1} · ${c.mg}mg THC</h3>
            <span class="dh-cb__case-price">${this.formatMoney(casePrice)}</span>
          </div>
          <div class="dh-cb__case-savings-row">
            <del class="dh-cb__case-was">${this.formatMoney(retailTotal)}</del>
            <span class="dh-cb__case-save-tag">Save ${savingsPct}%</span>
          </div>
          <div class="dh-cb__case-flavors">${flavorLines}</div>
          <div class="dh-cb__case-actions">
            <button type="button" class="dh-cb__case-action-btn" data-action="edit-case" data-case-index="${i}">Edit</button>
            <button type="button" class="dh-cb__case-action-btn dh-cb__case-action-btn--remove" data-action="remove-case" data-case-index="${i}">Remove</button>
          </div>
        `;

        listEl.appendChild(card);
      });

      // Update totals
      const completed = this.getCompletedCases();
      const totalEl = container.querySelector('[data-cases-total]');
      if (totalEl) {
        totalEl.textContent = `${completed.length} case${completed.length !== 1 ? 's' : ''} · ${this.formatMoney(this.getTotalPrice())}`;
      }
    }

    /* ========================================
       REVIEW PHASE
       ======================================== */

    renderReview() {
      const container = this.querySelector('[data-phase="review"]');
      if (!container) return;

      const itemsEl = container.querySelector('[data-review-items]');
      if (itemsEl) {
        itemsEl.innerHTML = '';
        this.state.cases.forEach((c, i) => {
          if (!this.isCaseComplete(c)) return;
          const products = this.getProductsForMg(c.mg);

          const caseBlock = document.createElement('div');
          caseBlock.className = 'dh-cb__review-case-block';

          let lines = '';
          for (const [handle, qty] of Object.entries(c.flavors)) {
            if (qty <= 0) continue;
            const product = products.find(p => p.handle === handle);
            if (product) {
              lines += `<div class="dh-cb__review-item">
                <span class="dh-cb__review-item-name">${qty}× ${this.cleanTitle(product.title)}</span>
                <span class="dh-cb__review-item-detail">${qty * this.cansPerPack} cans</span>
              </div>`;
            }
          }

          caseBlock.innerHTML = `
            <h4 class="dh-cb__review-case-title">Case ${i + 1} · ${c.mg}mg THC — ${this.formatMoney(this.getCasePrice(c.mg))}</h4>
            ${lines}
          `;
          itemsEl.appendChild(caseBlock);
        });
      }

      // Summary numbers
      const completed = this.getCompletedCases();
      const totalCans = completed.length * this.packsPerCase * this.cansPerPack;
      const total = this.getTotalPrice();
      const retail = this.getRetailValue();
      const savings = Math.max(0, retail - total);
      const shipping = this.getShippingFee();
      const grand = total + shipping;

      const set = (sel, val) => {
        const el = container.querySelector(sel);
        if (el) el.textContent = val;
      };

      set('[data-summary-cases]', `${completed.length} case${completed.length !== 1 ? 's' : ''}`);
      set('[data-summary-cans]', `${totalCans} cans`);
      set('[data-summary-retail]', this.formatMoney(retail));
      set('[data-summary-shipping]', shipping > 0 ? this.formatMoney(shipping) : 'Free');
      set('[data-summary-total]', this.formatMoney(grand));
      set('[data-summary-savings]', savings > 0 ? this.formatMoney(savings) : '$0.00');

      // Checkout CTA
      const checkoutCta = container.querySelector('[data-inline-cta="checkout"] .dh-cb__inline-cta-text');
      if (checkoutCta) checkoutCta.textContent = `Checkout — ${this.formatMoney(grand)}`;
    }

    /* ========================================
       BOTTOM BAR
       ======================================== */

    renderBottomBar() {
      const bar = this.querySelector('[data-bottom-bar]');
      if (!bar) return;

      const completed = this.getCompletedCases();
      const count = this.querySelector('[data-bar-count]');
      const price = this.querySelector('[data-bar-price]');
      const ctaBtn = this.querySelector('.dh-cb__bottom-bar-cta');
      const ctaText = this.querySelector('[data-bar-cta-text]');

      if (this.state.phase === 'pick-mg') {
        bar.hidden = completed.length === 0;
        if (count) count.textContent = `${completed.length} case${completed.length !== 1 ? 's' : ''} built`;
        if (price) price.textContent = this.formatMoney(this.getTotalPrice());
        if (ctaBtn) { ctaBtn.disabled = false; ctaBtn.dataset.action = 'review'; }
        if (ctaText) ctaText.textContent = 'Review Order';
      } else if (this.state.phase === 'pick-flavors') {
        const c = this.activeCase;
        const packs = c ? this.getCasePackCount(c) : 0;
        const remaining = this.packsPerCase - packs;
        bar.hidden = false;
        if (count) count.textContent = remaining > 0 ? `${packs}/${this.packsPerCase} packs` : 'Case complete!';
        if (price) price.textContent = '';
        if (ctaBtn) { ctaBtn.disabled = true; ctaBtn.dataset.action = ''; }
        if (ctaText) ctaText.textContent = remaining > 0 ? `${remaining} more to go` : 'Complete';
      } else if (this.state.phase === 'case-complete') {
        bar.hidden = completed.length === 0;
        if (count) count.textContent = `${completed.length} case${completed.length !== 1 ? 's' : ''} · ${completed.length * this.packsPerCase * this.cansPerPack} cans`;
        if (price) price.textContent = this.formatMoney(this.getTotalPrice());
        if (ctaBtn) { ctaBtn.disabled = false; ctaBtn.dataset.action = 'review'; }
        if (ctaText) ctaText.textContent = 'Review Order';
      } else if (this.state.phase === 'review') {
        const grand = this.getTotalPrice() + this.getShippingFee();
        bar.hidden = false;
        if (count) count.textContent = `${completed.length} case${completed.length !== 1 ? 's' : ''} · ${completed.length * this.packsPerCase * this.cansPerPack} cans`;
        if (price) price.textContent = this.formatMoney(grand);
        if (ctaBtn) { ctaBtn.disabled = false; ctaBtn.dataset.action = 'checkout'; }
        if (ctaText) ctaText.textContent = `Checkout — ${this.formatMoney(grand)}`;
      }
    }

    /* ========================================
       FLAVOR SCROLL (mobile horizontal scroll-snap)
       ======================================== */

    initFlavorScroll() {
      this.querySelectorAll('.dh-cb__flavor-grid').forEach(grid => {
        if ('IntersectionObserver' in window) {
          const observer = new IntersectionObserver(
            (entries) => {
              let best = null;
              entries.forEach(entry => {
                if (entry.isIntersecting && entry.intersectionRatio >= 0.7) {
                  if (!best || entry.intersectionRatio > best.intersectionRatio) best = entry;
                }
              });
              if (best) {
                const handle = best.target.dataset.handle;
                if (handle) this.setFocus(handle);
              }
            },
            { root: grid, threshold: [0.5, 0.7, 0.9] }
          );
          grid.querySelectorAll('.dh-bb__flavor-card').forEach(card => observer.observe(card));
        }
      });
    }

    renderFlavorDots(mg) {
      const dotsContainer = this.querySelector(`[data-flavor-dots="${mg}"]`);
      if (!dotsContainer) return;
      dotsContainer.innerHTML = '';

      const products = this.getProductsForMg(mg);
      if (products.length <= 1) return;

      const grid = this.querySelector(`.dh-cb__flavor-grid[data-flavor-group="${mg}"]`);

      products.forEach(p => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'dh-cb__flavor-dot';
        dot.dataset.dotHandle = p.handle;
        dot.setAttribute('aria-label', this.cleanTitle(p.title));
        if (p.handle === this._focusedHandle) dot.classList.add('is-active');
        dot.addEventListener('click', () => {
          this.setFocus(p.handle);
          if (grid) {
            const card = grid.querySelector(`.dh-bb__flavor-card[data-handle="${p.handle}"]`);
            if (card) card.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
          }
        });
        dotsContainer.appendChild(dot);
      });
    }

    updateFlavorDots() {
      this.querySelectorAll('.dh-cb__flavor-dot').forEach(dot => {
        const active = dot.dataset.dotHandle === this._focusedHandle;
        dot.classList.toggle('is-active', active);
      });
    }

    /* ========================================
       MEDIA STACK
       ======================================== */

    renderMediaStack(handle) {
      const container = this.querySelector('[data-media-stack]');
      if (!container) return;
      const product = [...this.products5mg, ...this.products10mg].find(p => p.handle === handle);
      if (!product || !product.media || product.media.length === 0) return;

      const productTitle = this.cleanTitle(product.title);
      const stack = document.createElement('div');
      stack.className = 'dh-cb__media-stack';

      product.media.forEach((media, index) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'dh-cb__media-item';
        btn.dataset.mediaOpen = '';
        btn.dataset.mediaHandle = handle;
        btn.dataset.mediaIndex = String(index);
        btn.setAttribute('aria-label', `Enlarge ${media.alt || productTitle}`);

        const img = document.createElement('img');
        img.src = media.src_1200;
        img.srcset = `${media.src_800} 800w, ${media.src_1200} 1200w, ${media.src_1600} 1600w`;
        img.sizes = '50vw';
        img.alt = media.alt || productTitle;
        img.loading = index === 0 ? 'eager' : 'lazy';
        img.width = media.width;
        img.height = media.height;
        img.className = 'dh-cb__media-image';

        btn.appendChild(img);
        stack.appendChild(btn);
      });

      container.innerHTML = '';
      container.appendChild(stack);
      container.scrollTop = 0;
    }

    /* ========================================
       LIGHTBOX
       ======================================== */

    bindLightbox() {
      this.lightbox = this.querySelector('[data-lightbox]');
      if (!this.lightbox) return;

      this.lightboxImage = this.lightbox.querySelector('[data-lightbox-image]');
      this.lightboxCounter = this.lightbox.querySelector('[data-lightbox-counter]');
      this.lightboxState = { handle: null, index: 0 };

      this.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-media-open]');
        if (!trigger) return;
        e.preventDefault();
        this.openLightbox(trigger.dataset.mediaHandle, parseInt(trigger.dataset.mediaIndex, 10) || 0, trigger);
      });

      this.lightbox.querySelector('[data-lightbox-close]')?.addEventListener('click', () => this.closeLightbox());
      this.lightbox.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => this.lightboxStep(-1));
      this.lightbox.querySelector('[data-lightbox-next]')?.addEventListener('click', () => this.lightboxStep(1));

      this.lightbox.addEventListener('click', (e) => {
        if (e.target === this.lightbox) this.closeLightbox();
      });

      document.addEventListener('keydown', (e) => {
        if (!this.lightbox || this.lightbox.hidden) return;
        if (e.key === 'Escape') { e.preventDefault(); this.closeLightbox(); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); this.lightboxStep(-1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); this.lightboxStep(1); }
      });
    }

    openLightbox(handle, index, returnEl) {
      const product = [...this.products5mg, ...this.products10mg].find(p => p.handle === handle);
      if (!product?.media?.length) return;
      this.lightboxState = { handle, index };
      this._lightboxReturnFocus = returnEl;
      this.updateLightboxImage();
      this.lightbox.hidden = false;
      document.body.classList.add('dh-cb-lightbox-open');
      this.lightbox.querySelector('[data-lightbox-close]')?.focus({ preventScroll: true });
    }

    closeLightbox() {
      if (!this.lightbox || this.lightbox.hidden) return;
      this.lightbox.hidden = true;
      document.body.classList.remove('dh-cb-lightbox-open');
      this._lightboxReturnFocus?.focus({ preventScroll: true });
    }

    lightboxStep(delta) {
      const product = [...this.products5mg, ...this.products10mg].find(p => p.handle === this.lightboxState.handle);
      if (!product?.media) return;
      const len = product.media.length;
      this.lightboxState.index = (this.lightboxState.index + delta + len) % len;
      this.updateLightboxImage();
    }

    updateLightboxImage() {
      const product = [...this.products5mg, ...this.products10mg].find(p => p.handle === this.lightboxState.handle);
      if (!product?.media) return;
      const media = product.media[this.lightboxState.index];
      if (!media) return;
      this.lightboxImage.src = media.src_1600 || media.src_1200;
      this.lightboxImage.alt = media.alt || product.title;
      if (this.lightboxCounter) {
        this.lightboxCounter.textContent = `${this.lightboxState.index + 1} / ${product.media.length}`;
      }
    }

    /* ========================================
       CHECKOUT
       ======================================== */

    async checkout() {
      const completed = this.getCompletedCases();
      if (completed.length === 0) return;

      const ctaBtn = this.querySelector('[data-inline-cta="checkout"]');
      const headerBtn = this.querySelector('.dh-cb__header-next');
      if (ctaBtn) { ctaBtn.disabled = true; ctaBtn.querySelector('.dh-cb__inline-cta-text').textContent = 'Processing...'; }
      if (headerBtn) headerBtn.disabled = true;

      try {
        // Get current cart
        const cartResponse = await fetch(window.routes.cart_url + '.js', {
          headers: { 'Content-Type': 'application/json' },
        });
        const cart = await cartResponse.json();

        // Remove existing case items
        const caseItems = cart.items.filter(item => item.properties?._hw_case === 'true');
        if (caseItems.length > 0) {
          const updates = {};
          caseItems.forEach(item => { updates[item.key] = 0; });
          await fetch(window.routes.cart_update_url + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates }),
          });
        }

        // Aggregate all flavors across cases (same handle = sum qty)
        const aggregated = {};
        for (const c of completed) {
          for (const [handle, qty] of Object.entries(c.flavors)) {
            if (qty <= 0) continue;
            aggregated[handle] = (aggregated[handle] || 0) + qty;
          }
        }

        // Add items
        const items = [];
        for (const [handle, qty] of Object.entries(aggregated)) {
          const product = [...this.products5mg, ...this.products10mg].find(p => p.handle === handle);
          if (!product) continue;
          items.push({
            id: product.variant_id,
            quantity: qty,
            properties: { _hw_case: 'true' },
          });
        }

        const addResponse = await fetch(window.routes.cart_add_url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items }),
        });

        const addResult = await addResponse.json();
        if (addResult.status) throw new Error(addResult.description || 'Failed to add items');

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'case-builder', cartData: addResult });
        }

        // Clear state and redirect
        sessionStorage.removeItem('dh-case-state');
        window.location.href = '/checkout';
      } catch (error) {
        console.error('[CaseBuilder] Checkout error:', error);
        if (ctaBtn) {
          ctaBtn.disabled = false;
          ctaBtn.querySelector('.dh-cb__inline-cta-text').textContent = 'Checkout';
        }
        if (headerBtn) headerBtn.disabled = false;
      }
    }

    /* ========================================
       ACCESSIBILITY
       ======================================== */

    announce(message) {
      const el = this.querySelector('.dh-cb__sr-status');
      if (el) { el.textContent = ''; requestAnimationFrame(() => { el.textContent = message; }); }
    }
  }

  customElements.define('dh-case-builder', DHCaseBuilder);
}
